import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { Route } from "@corpuscomplete/web/routes/_authenticated/groups/$id.index";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import type { GroupUI } from "@corpuscomplete/core-ui/auth/group";
import { GroupDetail } from "@corpuscomplete/core-ui/auth/group/detail";
import { Avatar, AvatarFallback, AvatarImage } from "@corpuscomplete/web-ui/components/avatar";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  LinkIcon,
  LogOutIcon,
  MoreHorizontalIcon,
  PencilIcon,
  SettingsIcon,
  TrashIcon,
  UserPlusIcon,
} from "lucide-react";

function MemberRow({ member, isSelf }: { member: GroupUI.Member; isSelf: boolean }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <Avatar>
        <AvatarImage src={member.profile?.image || undefined} />
        <AvatarFallback>{member.profile?.name.charAt(0).toUpperCase() ?? "?"}</AvatarFallback>
      </Avatar>
      <div className="flex items-center gap-2">
        <Text variant="sm">{member.profile?.name ?? "Unknown"}</Text>
        {isSelf && (
          <Text variant="xs" className="text-muted-foreground">
            (You)
          </Text>
        )}
      </div>
    </div>
  );
}

function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { createInvite } = GroupDetail.useMutations();

  const handleCreate = () => {
    void createInvite();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Invite</DialogTitle>
          <DialogDescription>
            Create a shareable invite link. The link will expire in 7 days and can only be used
            once. New members will join as basic members without any special roles.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate}>Create Invite</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteRow({ invite }: { invite: GroupUI.Invite }) {
  const permissions = GroupDetail.usePermissions();
  const { revokeInvite } = GroupDetail.useMutations();
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const link = `${window.location.origin}/join/${invite.token}`;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-3">
        <LinkIcon className="size-4 text-muted-foreground" />
        <div className="flex flex-col">
          <Text variant="sm">Invite Link</Text>
          <Text variant="xs" className="text-muted-foreground">
            Expires{" "}
            {invite.timeExpires
              ? invite.timeExpires.toLocaleString(undefined, { dateStyle: "medium" })
              : "Never"}
          </Text>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" onClick={copyLink}>
          {copied ? (
            <CheckIcon className="size-4 text-green-500" />
          ) : (
            <CopyIcon className="size-4" />
          )}
        </Button>
        {permissions.canRevokeInvite && (
          <Button variant="ghost" size="icon-sm" onClick={() => revokeInvite(invite.id)}>
            <TrashIcon className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function GroupDetailContent() {
  const navigate = useNavigate();
  const { groupId, userId } = GroupDetail.useContext();
  const group = GroupDetail.useGroup();
  const members = GroupDetail.useMembers();
  const { isLoading } = GroupDetail.useLoadingState();
  const canManage = GroupDetail.useCanManage();
  const currentUserIsMember = GroupDetail.useCurrentUserIsMember();
  const permissions = GroupDetail.usePermissions();
  const { isLastOwner } = GroupDetail.useOwnerStatus();
  const pendingInvites = GroupDetail.usePendingInvites();
  const { rename, leave, del } = GroupDetail.useMutations();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [newName, setNewName] = useState("");

  if (isLoading) {
    return null;
  }

  if (!group) {
    return (
      <Page>
        <PageBody>
          <PageContent>
            <div className="flex flex-1 items-center justify-center">
              <Text className="text-muted-foreground">Group not found</Text>
            </div>
          </PageContent>
        </PageBody>
      </Page>
    );
  }

  const groupName = group.name;

  const handleRename = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    rename(trimmed);
    setRenameOpen(false);
    setNewName("");
  };

  const handleDelete = () => {
    del();
    setDeleteOpen(false);
    void navigate({ to: "/groups" });
  };

  const handleLeave = () => {
    leave();
    setLeaveOpen(false);
    void navigate({ to: "/groups" });
  };

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text className="mr-auto">{groupName}</Text>
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              render={<Link to="/groups/$id/manage" params={{ id: groupId }} />}
            >
              <SettingsIcon className="mr-1.5 size-4" />
              Manage
            </Button>
          )}
          {permissions.canInvite && (
            <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlusIcon className="mr-1.5 size-4" />
              Invite
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
              <MoreHorizontalIcon className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {permissions.canRename && (
                <DropdownMenuItem
                  onClick={() => {
                    setNewName(groupName);
                    setRenameOpen(true);
                  }}
                >
                  <PencilIcon className="mr-2 size-4" />
                  Rename
                </DropdownMenuItem>
              )}
              {(permissions.canRename || currentUserIsMember) && <DropdownMenuSeparator />}
              {currentUserIsMember && (
                <DropdownMenuItem onClick={() => setLeaveOpen(true)}>
                  <LogOutIcon className="mr-2 size-4" />
                  Leave Group
                </DropdownMenuItem>
              )}
              {permissions.canDelete && (
                <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                  <TrashIcon className="mr-2 size-4" />
                  Delete Group
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </PageHeader>
        <PageContent>
          <div className="flex flex-col gap-6">
            <section>
              <Text variant="sm" className="mb-3 font-medium">
                Members ({members.length})
              </Text>
              <div className="flex flex-col divide-y">
                {members.map((member) => (
                  <MemberRow
                    key={member.userId}
                    member={member}
                    isSelf={member.userId === userId}
                  />
                ))}
              </div>
            </section>

            {pendingInvites.length > 0 && (
              <section>
                <Text variant="sm" className="mb-3 font-medium">
                  Pending Invites ({pendingInvites.length})
                </Text>
                <div className="flex flex-col divide-y">
                  {pendingInvites.map((invite) => (
                    <InviteRow key={invite.id} invite={invite} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </PageContent>
      </PageBody>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Group</DialogTitle>
            <DialogDescription>Enter a new name for this group.</DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{groupName}"? This action cannot be undone. All
              members will lose access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Group</DialogTitle>
            <DialogDescription>
              {isLastOwner
                ? "You are the last owner of this group. Transfer ownership to another member or delete the group to leave."
                : `Are you sure you want to leave "${groupName}"? You will lose access to all shared content.`}
            </DialogDescription>
          </DialogHeader>
          {isLastOwner && (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangleIcon className="size-4 shrink-0" />
              <Text variant="sm">Promote another member to owner first.</Text>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLeave} disabled={isLastOwner}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </Page>
  );
}

export function GroupPage() {
  const { id: groupId } = Route.useParams();
  const { userId } = useAuth();

  return (
    <GroupDetail.DataProvider groupId={groupId} userId={userId}>
      <GroupDetailContent />
    </GroupDetail.DataProvider>
  );
}
